import { ViewHook } from "phoenix_live_view";

const intersectionThreshold = 0.2;

// Matches the old Nuxt site: after a manual scroll (nav click or the initial deep-link jump) we
// ignore observer-driven activation for this long so it doesn't fight the in-flight scroll.
const manualScrollLockMs = 1000;

// Scroll-spy for the docs page. Mirrors the old Nuxt pages/api/[...slug].vue: one long page whose
// URL, sidebar highlight, and tab title track the section scrolled into view — all client-side via
// history.pushState, never a LiveView navigation. Back/forward onto a pushed entry is handled by
// LiveView's own popstate fallback (full remount -> handle_params -> this hook re-scrolls), so we
// never switch to push_patch.
class DocsScrollSpy extends ViewHook {
  // All state is (re)assigned in mounted(); declared with definite-assignment since the test
  // harness constructs the hook without running field initializers.
  private sections!: HTMLElement[];

  // Each section id maps to every nav link pointing at it (desktop sidebar + mobile picker).
  private navLinks!: Map<string, HTMLElement[]>;

  private mobileNav!: HTMLElement | null;

  private mobileLabel!: HTMLElement | null;

  private intersecting!: Set<HTMLElement>;

  private observer?: IntersectionObserver;

  private manualScrollLock!: boolean;

  private manualScrollTimer?: ReturnType<typeof setTimeout>;

  mounted() {
    this.onIntersect = this.onIntersect.bind(this);
    this.onNavLinkClick = this.onNavLinkClick.bind(this);

    this.sections = [...this.el.querySelectorAll<HTMLElement>("[data-doc-section]")];
    this.intersecting = new Set();

    // Nav links are siblings of #docs-page, not descendants, so query the whole document.
    this.navLinks = new Map();
    for (const link of document.querySelectorAll<HTMLElement>("[data-doc-nav-link]")) {
      const id = link.dataset.sectionId;
      if (id) {
        this.navLinks.set(id, [...(this.navLinks.get(id) ?? []), link]);
      }
    }

    this.mobileNav = document.querySelector<HTMLElement>("#docs-mobile-nav");
    this.mobileLabel = document.querySelector<HTMLElement>("[data-doc-mobile-label]");

    this.manualScrollLock = false;

    if (this.sections.length === 0) {
      return;
    }

    document.addEventListener("click", this.onNavLinkClick);

    this.observer = new IntersectionObserver(this.onIntersect, {
      threshold: intersectionThreshold,
    });
    for (const section of this.sections) {
      this.observer.observe(section);
    }

    this.scrollToInitialSection();

    if (this.el.dataset.activeSection) {
      this.scrollSidebarLinkIntoView(this.el.dataset.activeSection);
    }
  }

  destroyed() {
    document.removeEventListener("click", this.onNavLinkClick);

    this.observer?.disconnect();
    this.observer = undefined;

    if (this.manualScrollTimer) {
      clearTimeout(this.manualScrollTimer);
      this.manualScrollTimer = undefined;
    }
  }

  private scrollToInitialSection() {
    const activeId = this.el.dataset.activeSection;

    // The first section already sits at the top; only deep links need an instant jump.
    if (!activeId || activeId === this.sections[0].id) {
      return;
    }

    const target = this.sections.find((section) => section.id === activeId);
    if (target) {
      this.lockManualScroll();
      target.scrollIntoView({ behavior: "auto" });
    }
  }

  private onIntersect(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.intersecting.add(entry.target as HTMLElement);
      } else {
        this.intersecting.delete(entry.target as HTMLElement);
      }
    }

    if (this.manualScrollLock) {
      return;
    }

    const active = this.sections.find((section) => this.intersecting.has(section));
    if (active) {
      this.setActive(active);
    }
  }

  private onNavLinkClick(event: MouseEvent) {
    const link = (event.target as HTMLElement).closest<HTMLElement>("[data-doc-nav-link]");
    if (!link) {
      return;
    }

    const section = this.sections.find((candidate) => candidate.id === link.dataset.sectionId);
    if (!section) {
      return;
    }

    event.preventDefault();
    this.lockManualScroll();
    section.scrollIntoView({ behavior: "smooth" });
    this.setActive(section);
    this.closeMobileNav();
  }

  private closeMobileNav() {
    if (!this.mobileNav) {
      return;
    }

    this.mobileNav.dataset.state = "closed";
    for (const button of this.mobileNav.querySelectorAll("button[aria-expanded]")) {
      button.setAttribute("aria-expanded", "false");
    }
  }

  private setActive(section: HTMLElement) {
    for (const [id, links] of this.navLinks) {
      for (const link of links) {
        link.toggleAttribute("data-active", id === section.id);
      }
    }

    this.scrollSidebarLinkIntoView(section.id);

    const { docTitle, docPath, docLabel } = section.dataset;

    if (this.mobileLabel && docLabel) {
      this.mobileLabel.textContent = docLabel;
    }

    if (docTitle) {
      document.title = docTitle;
    }

    if (docPath && window.location.pathname !== docPath) {
      window.history.pushState({}, "", docPath);
    }
  }

  private scrollSidebarLinkIntoView(sectionId: string) {
    for (const link of this.navLinks.get(sectionId) ?? []) {
      if (link.closest("#docs-sidebar")) {
        link.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }
  }

  private lockManualScroll() {
    this.manualScrollLock = true;

    if (this.manualScrollTimer) {
      clearTimeout(this.manualScrollTimer);
    }

    this.manualScrollTimer = setTimeout(() => {
      this.manualScrollLock = false;
    }, manualScrollLockMs);
  }
}

export default DocsScrollSpy;
